/**
 * PATH: packages/email-hub/src/feature/components/InboxSweepBanner.tsx
 * FILE_ID: EMAIL-INBOX-SWEEP-BANNER-001
 * PURPOSE: Post-sweep banner — tells the operator the one-time onboarding
 *          sweep filed N older emails as Done/Junk, with one-click Undo.
 * WHY: A business mailbox that silently loses 150k rows from view without
 *      explanation looks like data loss. The banner is the trust surface:
 *      what happened, where the mail went (Done/Junk folders — nothing
 *      deleted), and an exact-restore Undo.
 * LANDMINE: GET /api/emails/inbox-sweep/ is org-wide. Without the
 *      visible-account gate below, a disconnected / empty Focus user still
 *      saw another mailbox's "Inbox cleaned up" counts.
 * LANDMINE (BUG-2026-07-12-SWEEP-BANNER-DISMISS-REPOPS): dismiss used to
 *      be a per-sweep-id list in an unscoped localStorage key. That failed
 *      when (a) allIds raced empty and only one id was written, (b) the
 *      operator bounced between dashboard.quotech.ai and email.quotech.ai
 *      (different origins), or (c) a later remount re-fetched before the
 *      id list stuck. "X" now stamps a durable until-watermark (tenant-
 *      scoped local cache + server view-preference) so every sweep that
 *      already ran stays silent; only a NEW sweep after the ack can reappear.
 * LANDMINE (BUG-2026-07-14-SWEEP-BANNER-STALE-HYDRATE): hydrate must
 *      re-read local + dismissRef AFTER every await. Snapshotting dismiss
 *      state before `fetchServerState` / `inbox-sweep` and applying it
 *      later resurfaced the banner when the operator hit X while the
 *      request was in flight.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Sparkles, Undo2, X } from 'lucide-react'

import {
  fetchServerState,
  loadCachedState,
  saveCachedState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'
import { emailApi } from '../lib/api'
import { useEmailAccounts } from '../hooks/useEmails'

export interface InboxSweep {
  id: number
  account_id: number
  trigger: string
  status: string
  keep_count: number
  kept_count: number
  done_count: number
  junk_count: number
  catchup_count: number
  executed_at: string | null
  reverted_at: string | null
}

/** Tenant-scoped via dbViewPreference's tlsKey — survives origin hops
 *  (dashboard ↔ email hubs) once the server slice hydrates. */
export const DISMISS_SCOPE = 'email.inbox_sweep_banner'
/** Legacy unscoped key from the first ship — migrated on read. */
const LEGACY_DISMISS_KEY = 'qt-inbox-sweep-dismissed'
/** Banner only shows for sweeps executed within this window. */
const SHOW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000
/** Auto-ack after this delay so the banner does not nag (same durable dismiss as X). */
export const AUTO_DISMISS_MS = 20_000

export interface SweepDismissState {
  /** Epoch ms. Hide every sweep whose executed_at is <= this watermark. */
  until: number
  /** Legacy/compat id list — still honoured, no longer the primary signal. */
  ids: number[]
}

const DEFAULT_DISMISS: SweepDismissState = { until: 0, ids: [] }

export function coerceDismissState(
  raw: unknown,
  defaults: SweepDismissState = DEFAULT_DISMISS,
): SweepDismissState {
  if (Array.isArray(raw)) {
    // Legacy shape: bare id list.
    const ids = raw.filter((x): x is number => typeof x === 'number')
    return { until: defaults.until, ids }
  }
  if (!raw || typeof raw !== 'object') return { ...defaults }
  const obj = raw as { until?: unknown; ids?: unknown }
  const until = typeof obj.until === 'number' && Number.isFinite(obj.until) ? obj.until : defaults.until
  const ids = Array.isArray(obj.ids)
    ? obj.ids.filter((x): x is number => typeof x === 'number')
    : defaults.ids
  return { until, ids }
}

function readLegacyIds(): number[] {
  try {
    const raw = localStorage.getItem(LEGACY_DISMISS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter((x): x is number => typeof x === 'number')
      : []
  } catch {
    return []
  }
}

/** Merge legacy unscoped ids into the tenant-scoped cache once. */
export function loadDismissState(): SweepDismissState {
  const cached = loadCachedState(DISMISS_SCOPE, DEFAULT_DISMISS, coerceDismissState)
  const legacy = readLegacyIds()
  if (legacy.length === 0) return cached
  const mergedIds = [...new Set([...cached.ids, ...legacy])].slice(-100)
  const merged = { until: cached.until, ids: mergedIds }
  saveCachedState(DISMISS_SCOPE, merged)
  try {
    localStorage.removeItem(LEGACY_DISMISS_KEY)
  } catch {
    /* fail-soft */
  }
  return merged
}

export function persistDismissState(state: SweepDismissState): void {
  saveCachedState(DISMISS_SCOPE, state)
  scheduleServerSave(DISMISS_SCOPE, state, 0)
}

/** Strongest watermark wins — used after awaits so an X click during
 *  hydrate cannot be overwritten by a stale pre-await snapshot. */
export function mergeDismissStates(
  ...parts: Array<SweepDismissState | null | undefined>
): SweepDismissState {
  let until = 0
  const ids = new Set<number>()
  for (const part of parts) {
    if (!part) continue
    until = Math.max(until, part.until)
    for (const id of part.ids) ids.add(id)
  }
  return { until, ids: [...ids].slice(-100) }
}

/** Every sweep the banner WOULD announce (executed, filed something, not
 *  reverted, inside the window) — regardless of dismissal state. The X
 *  button acknowledges ALL of them at once via a watermark.
 *
 *  BUG-2026-07-08-SWEEP-BANNER-X-WHACK-A-MOLE: the retro-clear created one
 *  sweep row PER MAILBOX. Dismissing only the displayed sweep meant the
 *  next mount surfaced the NEXT undismissed row.
 */
export function announceableSweepIds(
  sweeps: InboxSweep[],
  now: number = Date.now(),
): number[] {
  return sweeps
    .filter(s =>
      s.status === 'executed' &&
      !s.reverted_at &&
      s.done_count + s.junk_count + s.catchup_count > 0 &&
      s.executed_at !== null &&
      now - new Date(s.executed_at).getTime() >= 0 &&
      now - new Date(s.executed_at).getTime() <= SHOW_WINDOW_MS,
    )
    .map(s => s.id)
}

export function isSweepDismissed(
  sweep: InboxSweep,
  dismissed: SweepDismissState,
): boolean {
  if (dismissed.ids.includes(sweep.id)) return true
  if (!sweep.executed_at || dismissed.until <= 0) return false
  return new Date(sweep.executed_at).getTime() <= dismissed.until
}

/** Pick the sweep worth announcing: executed recently, filed something,
 *  not reverted, not dismissed (by id OR until-watermark). */
export function pickAnnounceableSweep(
  sweeps: InboxSweep[],
  dismissed: SweepDismissState | number[],
  now: number = Date.now(),
): InboxSweep | null {
  const state = Array.isArray(dismissed)
    ? coerceDismissState(dismissed)
    : dismissed
  for (const sweep of sweeps) {
    if (sweep.status !== 'executed') continue
    if (sweep.reverted_at) continue
    if (sweep.done_count + sweep.junk_count + sweep.catchup_count === 0) continue
    if (isSweepDismissed(sweep, state)) continue
    if (!sweep.executed_at) continue
    const age = now - new Date(sweep.executed_at).getTime()
    if (age < 0 || age > SHOW_WINDOW_MS) continue
    return sweep
  }
  return null
}

/** Build the dismiss payload for an X click — watermark covers every
 *  currently announceable sweep so a remount cannot resurface them. */
export function buildDismissAck(
  sweeps: InboxSweep[],
  previous: SweepDismissState,
  now: number = Date.now(),
): SweepDismissState {
  const announceable = sweeps.filter(s => announceableSweepIds([s], now).includes(s.id))
  const executedAt = announceable
    .map(s => (s.executed_at ? new Date(s.executed_at).getTime() : 0))
    .filter(t => t > 0)
  const until = Math.max(previous.until, now, ...executedAt)
  const ids = [...new Set([
    ...previous.ids,
    ...announceable.map(s => s.id),
  ])].slice(-100)
  return { until, ids }
}

/** Real EmailAccount ids the current viewer can see (skips synthetic
 *  legacy facet buckets where `account_id === null`). */
export function visibleConnectedAccountIds(
  accounts: ReadonlyArray<{ id: number; account_id?: number | null }>,
): Set<number> {
  const ids = new Set<number>()
  for (const a of accounts) {
    // Synthetic legacy rows use account_id: null and a negative id —
    // sweeps always reference a real EmailAccount row.
    if (a.account_id === null) continue
    const id = typeof a.account_id === 'number' ? a.account_id : a.id
    if (id > 0) ids.add(id)
  }
  return ids
}

/** Drop org-wide sweeps that do not belong to a mailbox this user can see. */
export function sweepsForVisibleAccounts(
  sweeps: InboxSweep[],
  accountIds: ReadonlySet<number>,
): InboxSweep[] {
  if (accountIds.size === 0) return []
  return sweeps.filter((s) => accountIds.has(s.account_id))
}

export function useInboxSweepBanner() {
  const { accounts, loading: accountsLoading } = useEmailAccounts()
  // Stable key so a 60s accounts poll with the same ids does not re-hit
  // the sweep endpoint (and does not flicker the banner).
  const visibleIdsKey = useMemo(() => {
    const ids = [...visibleConnectedAccountIds(accounts)].sort((a, b) => a - b)
    return ids.join(',')
  }, [accounts])
  const [sweep, setSweep] = useState<InboxSweep | null>(null)
  const [reverting, setReverting] = useState(false)
  // Bumped when Clean up inbox finishes so the banner re-hydrates immediately.
  const [refreshNonce, setRefreshNonce] = useState(0)
  // Ref — not React state — so dismiss never races an empty allIds and
  // only writes the single displayed id (the prior whack-a-mole regression).
  const sweepsRef = useRef<InboxSweep[]>([])
  const dismissRef = useRef<SweepDismissState>(loadDismissState())

  useEffect(() => {
    const onExecuted = () => setRefreshNonce((n) => n + 1)
    window.addEventListener('quotech:inbox-sweep-executed', onExecuted)
    return () => window.removeEventListener('quotech:inbox-sweep-executed', onExecuted)
  }, [])

  useEffect(() => {
    // Wait until mailbox list has settled so we never flash an org-wide
    // sweep for a mailbox the viewer cannot see / has disconnected.
    if (accountsLoading) return
    let cancelled = false
    const visibleIds = new Set(
      visibleIdsKey
        ? visibleIdsKey.split(',').map((s) => Number(s)).filter((n) => n > 0)
        : [],
    )
    // No connected mailboxes → never announce (ghost banner root cause).
    if (visibleIds.size === 0) {
      sweepsRef.current = []
      setSweep(null)
      return () => { cancelled = true }
    }

    const apply = (dismissed: SweepDismissState, sweeps: InboxSweep[]) => {
      if (cancelled) return
      sweepsRef.current = sweeps
      const merged = mergeDismissStates(dismissRef.current, dismissed)
      dismissRef.current = merged
      setSweep(pickAnnounceableSweep(sweeps, merged))
    }

    // Hydrate durable server ack first (cross-origin / cross-device), then
    // fetch sweeps against the merged dismiss state.
    void (async () => {
      const server = await fetchServerState(
        DISMISS_SCOPE,
        DEFAULT_DISMISS,
        coerceDismissState,
      )
      // Re-read AFTER the await — X may have stamped a stronger watermark
      // while fetchServerState was in flight (stale-hydrate race).
      let dismissed = mergeDismissStates(
        loadDismissState(),
        dismissRef.current,
        server,
      )
      if (server && (
        server.until > 0 || server.ids.length > 0
      )) {
        saveCachedState(DISMISS_SCOPE, dismissed)
      }
      dismissRef.current = dismissed
      if (cancelled) return
      try {
        const data = await emailApi<{ sweeps: InboxSweep[] }>('/api/emails/inbox-sweep/')
        if (cancelled) return
        // Same race on the sweeps GET — merge again before announce.
        dismissed = mergeDismissStates(
          dismissed,
          loadDismissState(),
          dismissRef.current,
        )
        dismissRef.current = dismissed
        apply(dismissed, sweepsForVisibleAccounts(data?.sweeps ?? [], visibleIds))
      } catch {
        /* endpoint unavailable — banner simply doesn't show */
      }
    })()

    return () => { cancelled = true }
  }, [accountsLoading, visibleIdsKey, refreshNonce])

  const dismiss = useCallback(() => {
    const next = buildDismissAck(sweepsRef.current, dismissRef.current)
    dismissRef.current = next
    persistDismissState(next)
    setSweep(null)
  }, [])

  const revert = useCallback(async () => {
    if (!sweep || reverting) return null
    setReverting(true)
    try {
      const res = await emailApi<{ restored: number }>(
        `/api/emails/inbox-sweep/${sweep.id}/revert/`,
        { method: 'POST', body: JSON.stringify({}) },
      )
      // Undo only acknowledges the sweep it reverted — if OTHER mailboxes
      // were also swept the operator should still hear about those.
      const next: SweepDismissState = {
        until: dismissRef.current.until,
        ids: [...new Set([...dismissRef.current.ids, sweep.id])].slice(-100),
      }
      dismissRef.current = next
      persistDismissState(next)
      setSweep(null)
      return res
    } finally {
      setReverting(false)
    }
  }, [sweep, reverting])

  return { sweep, dismiss, revert, reverting }
}

export function InboxSweepBanner({ onReverted }: { onReverted?: () => void }) {
  const { sweep, dismiss, revert, reverting } = useInboxSweepBanner()

  useEffect(() => {
    if (!sweep) return
    const timer = window.setTimeout(() => {
      dismiss()
    }, AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [sweep?.id, dismiss])

  if (!sweep) return null

  const filed = sweep.done_count + sweep.catchup_count
  const parts: string[] = []
  if (filed > 0) parts.push(`${filed.toLocaleString()} older email${filed === 1 ? '' : 's'} filed as Done`)
  if (sweep.junk_count > 0) parts.push(`${sweep.junk_count.toLocaleString()} as Junk`)

  return (
    <div
      data-quotech-inbox-sweep-banner
      className="flex items-center gap-2 px-3 py-2 text-xs rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/15 dark:text-emerald-100"
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0">
        <span className="font-medium">Inbox cleaned up.</span>{' '}
        {parts.join(' and ')} — the latest {sweep.keep_count} stay here.
        Nothing was deleted: everything is in the Done and Junk folders.
      </span>
      <div className="flex items-center gap-1.5 ml-auto shrink-0">
        <button
          type="button"
          onClick={() => { revert().then(() => onReverted?.()) }}
          disabled={reverting}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-emerald-800/80 dark:text-emerald-100/80 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/25 disabled:opacity-50 underline-offset-2 hover:underline"
          title="Restore every swept email to exactly its prior state"
        >
          <Undo2 className="h-3 w-3" />
          {reverting ? 'Restoring…' : 'Undo'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="flex items-center gap-1 px-2.5 py-0.5 rounded bg-white/70 dark:bg-white/10 border border-emerald-300 dark:border-emerald-800 hover:bg-white font-medium"
          title="Got it — keep the cleanup, hide this banner"
        >
          Got it
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
          title="Dismiss"
          aria-label="Dismiss inbox cleanup banner"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
